import { useEffect, useMemo } from 'react';

export interface SeoProps {
    /** When set, overrides JSON injected by Laravel (`#pl-marketing-seo`). */
    title?: string;
    description?: string;
    /**
     * Set to `false` when `title` already ends its own sentence with the
     * site name (e.g. "Voor wie PlanLogic is — vijf omgevingen") rather
     * than expecting it appended as a mechanical suffix. Defaults to
     * `true`, matching every other page. Ignored when the injected
     * payload provides its own `append_site_name` (config is then the
     * single source, per `config/marketing-seo.php`'s doc-comment).
     */
    appendSiteName?: boolean;
}

type InjectedPayload = {
    title: string;
    description: string;
    site_name: string;
    append_site_name: boolean;
};

/**
 * The one client-side place that turns a bare page title into what the
 * browser tab actually shows. Mirrors `App\Support\Seo\PageTitle::format()`
 * (PHP) exactly — `config/marketing-seo.php` holds only the bare title;
 * this is where " — {siteName}" gets appended, once. Exported so a page
 * passing `title`/`description` as props (Signup, Login, ErrorPage,
 * Environment) goes through the identical rule instead of hand-rolling
 * its own suffix, which is exactly how "X — PlanLogic — PlanLogic"
 * happened when a config title already carried the suffix (9 sept 2026).
 */
export function formatPageTitle(title: string, siteName: string, appendSiteName = true): string {
    if (!appendSiteName || title === siteName || title.endsWith(` — ${siteName}`)) {
        return title;
    }
    return `${title} — ${siteName}`;
}

function readInjectedPayload(): InjectedPayload | null {
    if (typeof document === 'undefined') {
        return null;
    }
    const el = document.getElementById('pl-marketing-seo');
    const raw = el?.textContent?.trim();
    if (!raw) {
        return null;
    }
    try {
        const j = JSON.parse(raw) as Partial<InjectedPayload>;
        if (typeof j.title === 'string' && typeof j.description === 'string') {
            return {
                title: j.title,
                description: j.description,
                site_name: typeof j.site_name === 'string' ? j.site_name : 'PlanLogic',
                append_site_name: typeof j.append_site_name === 'boolean' ? j.append_site_name : true,
            };
        }
    } catch {
        return null;
    }
    return null;
}

/**
 * Syncs document title and meta description with Laravel-injected JSON
 * (single source: config/marketing-seo.php) or optional props for routes
 * that do not inject (e.g. non-marketing shells).
 */
export default function Seo(props: SeoProps = {}) {
    const { title: titleProp, description: descriptionProp, appendSiteName: appendSiteNameProp } = props;
    const injected = useMemo(() => readInjectedPayload(), []);

    const title = titleProp ?? injected?.title;
    const description = descriptionProp ?? injected?.description;
    const siteName = injected?.site_name ?? 'PlanLogic';
    // The injected payload (config/marketing-seo.php) is the source of
    // truth when present; the prop only matters for pages that bypass
    // it entirely (Signup, Login — served outside MarketingViewController).
    const appendSiteName = injected?.append_site_name ?? appendSiteNameProp ?? true;

    useEffect(() => {
        if (!title || !description) {
            return;
        }

        document.title = formatPageTitle(title, siteName, appendSiteName);

        let meta = document.querySelector('meta[name="description"]');
        if (!meta) {
            meta = document.createElement('meta');
            meta.setAttribute('name', 'description');
            document.head.appendChild(meta);
        }

        meta.setAttribute('content', description);
    }, [description, siteName, title, appendSiteName]);

    return null;
}
